import {
  ToggleButtonGroup as MuiToggleButtonGroup,
  ToggleButtonGroupProps as MuiToggleButtonGroupProps,
  ToggleButton,
  ToggleButtonProps,
} from '@mui/material';
import { isEqual } from 'lodash';
import { MouseEvent, ReactNode } from 'react';

type ButtonValue = string | number | boolean;

type Button = {
  value: ButtonValue;
  content: ReactNode;
  buttonProps?: Partial<ToggleButtonProps>;
};

export type ToggleButtonGroupProps = {
  spacing?: number;
  buttons: Array<Button>;
  selectedValue: ButtonValue | ButtonValue[];
  onChange: (value: ButtonValue | ButtonValue[] | null) => void;
  exclusive?: boolean;
} & Partial<MuiToggleButtonGroupProps>;

export const ToggleButtonGroup = ({
  spacing = 0,
  buttons,
  selectedValue,
  onChange,
  exclusive = false,
  ...props
}: ToggleButtonGroupProps) => {
  const handleChange = (_: MouseEvent<HTMLElement>, newValue: ButtonValue | ButtonValue[]) => {
    const hasValueChanged = exclusive
      ? newValue !== selectedValue
      : !isEqual([...(newValue as ButtonValue[])].sort(), [...(selectedValue as ButtonValue[])].sort());

    onChange(hasValueChanged ? newValue : null);
  };

  const isSelected = (value: ButtonValue) =>
    Array.isArray(selectedValue) ? selectedValue?.includes(value) : selectedValue === value;

  return (
    <MuiToggleButtonGroup
      data-testid="toggle-button-group"
      value={selectedValue}
      exclusive={exclusive}
      onChange={handleChange}
      aria-label="toggle-button-group"
      {...props}
    >
      {buttons.map(({ value, content, buttonProps }, index) => {
        return (
          <ToggleButton
            data-testid={`toggle-button-${index}`}
            key={`unit-${index}`}
            value={value}
            selected={isSelected(value)}
            sx={{
              color: (theme) => theme.palette.grey[600],
              ...(spacing
                ? {
                    border: (theme) => `1px solid ${theme.palette.grey[300]} !important`,
                    borderRadius: 'inherit !important',
                    marginLeft: '0 !important',
                    '&:not(:last-of-type)': {
                      marginRight: (theme) => theme.spacing(spacing),
                    },
                  }
                : {}),
            }}
            {...(buttonProps ?? {})}
          >
            {content}
          </ToggleButton>
        );
      })}
    </MuiToggleButtonGroup>
  );
};
